/**
 * MobileCategoryEffect — the per-category effect list (DD-RO-MOBILE-TRADE-MODAL-0728).
 *
 * The known-good rendering lifted from the FA-swap modal's "Net Category Effect" block so the trade detail
 * modal can REUSE it (not reinvent a path). Every row shows the projected raw stat delta AND the standings-
 * point impact (with the rank move) — so a category whose RANK didn't move (points 0) still shows its real
 * raw swing, instead of reading as a misleading "0".
 *
 * rows: [{ category, stat_delta, rank_before, rank_after, points_delta | pts_delta }]
 *   - net_category_effect (FA)          → points_delta
 *   - true_category_impact (trade)      → pts_delta   (the SAME reprojection: score_value_overlay_by_category)
 */
import React from 'react';
import {
  C, SP, FS, PRIMARY, BORDER, BORDER_SOFT, NUM, sectionHeaderStyle,
} from './optimizerTokens';

// DD-RO-MOBILE-TRADE-LOGIC-0729 Root B (sibling of the DD-RO-MOBILE-DETAIL / OptimizerFlyout rounding):
// counting-category projected deltas show as WHOLE numbers (SB +19.3349 → +19); ratio stats
// (AVG/OPS/ERA/WHIP, |delta| < 1) keep their 3-decimal form. Sign preserved.
const fmtStatDelta = (sd) => {
  if (sd == null) return '';
  if (Math.abs(sd) < 1) return sd.toFixed(3).replace(/^(-?)0\./, '$1.');   // ratio: ".031" / "-.031"
  return (sd > 0 ? '+' : '') + Math.round(sd);                            // counting: "+19" / "-3"
};

export default function MobileCategoryEffect({
  rows,
  title = 'Net Category Effect',
  testid = 'net-category-effect',
  emptyLabel = 'No category moves.',
  // DD-RO-MARGINAL-REPROJECTION-0729 item 2: BOTH players' per-category ROS lines, so the row reads
  // out-proj → in-proj  (Δ diff), not a lone rank arrow. Absent → the row shows just the diff (back-compat).
  outProj = null,
  inProj = null,
}) {
  const hasLines = !!(outProj || inProj);
  const projVal = (m, c) => { const v = m && m[c]; return v == null ? '—' : v; };
  const shs = sectionHeaderStyle(PRIMARY);
  // DD-RO-MOBILECATEGORYEFFECT-CRASH-0729: fail SOFT — a non-array `rows` (e.g. a pre-enrichment swap_chain
  // step whose category_impact is still the {category: points} DICT before the backend deploys) must render
  // as empty, NEVER `.map`-throw a white-screen. Callers normalize to the row shape; this is the backstop.
  const list = Array.isArray(rows) ? rows : [];
  return (
    <>
      <div style={shs.row}><span style={shs.bar} /><span style={shs.label}>{title}</span></div>
      <div style={{ borderTop: BORDER }} data-testid={testid}>
        {list.length === 0 && (
          <div style={{ padding: `6px 0`, fontSize: FS.xs, color: C.neutral[500] }}>{emptyLabel}</div>
        )}
        {list.map((r) => {
          const dpts = r.points_delta != null ? r.points_delta : r.pts_delta;   // FA vs trade field name
          return (
            <div key={r.category} data-testid="category-effect-row"
              style={{ display: 'flex', alignItems: 'center', gap: SP.xs, padding: `6px 0`, borderBottom: BORDER_SOFT }}>
              <span style={{ width: 34, fontSize: FS.xs, fontWeight: 700, color: C.neutral[300] }}>{r.category}</span>
              {hasLines && (
                <span style={{ flex: 1, fontSize: FS['2xs'], color: C.neutral[500], ...NUM }} data-testid="cat-proj-line">
                  {projVal(outProj, r.category)} <span style={{ color: C.neutral[600] }}>→</span> {projVal(inProj, r.category)}
                </span>
              )}
              <span style={{ flex: hasLines ? 0 : 1, minWidth: 40, textAlign: hasLines ? 'right' : 'left', fontSize: FS.xs, color: C.neutral[400], ...NUM }}>
                {fmtStatDelta(r.stat_delta)}
              </span>
              <span style={{ fontSize: FS['2xs'], color: C.neutral[500], ...NUM }}>{r.rank_before}</span>
              <span style={{ color: C.neutral[600] }}>→</span>
              <span style={{ fontSize: FS['2xs'], fontWeight: 700, ...NUM,
                color: r.rank_after < r.rank_before ? C.success : C.neutral[400] }}>{r.rank_after}</span>
              <span style={{ width: 26, textAlign: 'right', fontSize: FS.xs, fontWeight: 800, ...NUM,
                color: dpts > 0 ? C.success : dpts < 0 ? C.error : C.neutral[500] }}>
                {dpts > 0 ? `+${dpts}` : dpts === 0 ? '—' : dpts}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}
